// Public marketing landing page for Managen.
// Rendered into #root at the root URL (/) for unauthenticated visitors.
// All CTAs route into the app (/app) and trigger the signup/signin flow.

const CONTENT_TYPES = [
  'Blog', 'Social Packs', 'Email Sequences', 'Ad Copy',
  'Video Scripts', 'Newsletters', 'Landing Pages', 'Threads'
];

const PROBLEMS = [
  'You spend more time writing than growing',
  "You're stitching together ChatGPT, Notion, Google Docs, and three other tools",
  'Following up with prospects falls through the cracks',
  'Invoicing clients takes longer than it should',
  "You can't scale without hiring"
];

const FEATURES = [
  { n: '01', title: 'Generate', body: '8 content types briefed to sound like your client’s brand.' },
  { n: '02', title: 'Manage Clients', body: 'Save brand voice per client, auto-fills every generation.' },
  { n: '03', title: 'Bulk Run', body: 'Produce a full month of content in one run.' },
  { n: '04', title: 'Invoice', body: 'Create and export polished PDF invoices without leaving the app.' },
  { n: '05', title: 'Outreach Hub', body: 'Find prospects, write personalized cold emails and DMs, track replies.' }
];

const PLANS = [
  {
    name: 'Free Trial', price: '$0', per: '', featured: false,
    features: ['5 generations', 'No card required', 'Try every feature'],
    cta: 'Start Free', mode: 'signup'
  },
  {
    name: 'Starter', price: '$29', per: '/mo', featured: true,
    features: ['50 generations / mo', 'All features included', 'Clients, bulk, invoices & outreach'],
    cta: 'Get Started', mode: 'signup'
  },
  {
    name: 'Pro', price: '$59', per: '/mo', featured: false,
    features: ['Unlimited generations', 'Everything in Starter', 'Priority throughput'],
    cta: 'Go Pro', mode: 'signup'
  }
];

function esc(s) {
  return String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

/**
 * Render the landing page.
 * @param {(mode:'signup'|'signin')=>void} onCta  Called when any CTA is clicked; routes into the app.
 */
export function showLanding(onCta) {
  document.body.classList.add('landing-active');
  document.getElementById('root').classList.add('landing-active');

  const typeBar = CONTENT_TYPES.map(t => `<span class="lp-chip">${esc(t)}</span>`).join('<span class="lp-dot">·</span>');

  document.getElementById('root').innerHTML = `
  <div class="lp">
    <nav class="lp-nav">
      <a class="lp-logo" href="#top" aria-label="Managen home">
        <span class="lp-logo-mark">M</span>
        <span class="lp-logo-name">MANA<span class="ll">GEN</span></span>
      </a>
      <div class="lp-nav-actions">
        <button class="btn ghost sm" data-cta="signin">Sign In</button>
        <button class="btn primary sm" data-cta="signup">Start Free</button>
      </div>
    </nav>

    <header class="lp-hero" id="top">
      <div class="lp-hero-glow"></div>
      <div class="lp-container lp-reveal">
        <div class="lp-eyebrow">The all-in-one content agency OS</div>
        <h1 class="lp-h1">Run Your Content Agency<br>From One Tab</h1>
        <p class="lp-sub">Managen generates, organizes, invoices, and pitches — everything a freelance content writer or small agency needs, without the 5-tab chaos.</p>
        <div class="lp-hero-cta">
          <button class="btn primary lp-btn-lg" data-cta="signup">Start Free Trial</button>
          <div class="lp-subcta">5 free generations · No card required</div>
        </div>
      </div>
    </header>

    <div class="lp-typebar" aria-hidden="true">
      <div class="lp-typebar-track">
        <div class="lp-typebar-group">${typeBar}</div>
        <div class="lp-typebar-group">${typeBar}</div>
      </div>
    </div>

    <section class="lp-section">
      <div class="lp-container lp-reveal">
        <h2 class="lp-h2">Sound familiar?</h2>
        <div class="lp-problems">
          ${PROBLEMS.map(p => `
            <div class="lp-problem">
              <span class="lp-problem-x">✕</span>
              <span>${esc(p)}</span>
            </div>`).join('')}
        </div>
      </div>
    </section>

    <section class="lp-section lp-section-alt">
      <div class="lp-container lp-reveal">
        <h2 class="lp-h2">Everything you need. Nothing you don’t.</h2>
        <div class="lp-features">
          ${FEATURES.map(f => `
            <div class="lp-feature">
              <div class="lp-feature-n">${f.n}</div>
              <div class="lp-feature-title">${esc(f.title)}</div>
              <div class="lp-feature-body">${esc(f.body)}</div>
            </div>`).join('')}
        </div>
      </div>
    </section>

    <section class="lp-section" id="pricing">
      <div class="lp-container lp-reveal">
        <h2 class="lp-h2">Simple pricing. No surprises.</h2>
        <div class="lp-plans">
          ${PLANS.map(p => `
            <div class="lp-plan${p.featured ? ' featured' : ''}">
              ${p.featured ? '<div class="lp-plan-badge">Most popular</div>' : ''}
              <div class="lp-plan-name">${esc(p.name)}</div>
              <div class="lp-plan-price">${esc(p.price)}<span class="lp-plan-per">${esc(p.per)}</span></div>
              <ul class="lp-plan-list">
                ${p.features.map(f => `<li>${esc(f)}</li>`).join('')}
              </ul>
              <button class="btn ${p.featured ? 'primary' : ''} lp-plan-cta" data-cta="${p.mode}">${esc(p.cta)}</button>
            </div>`).join('')}
        </div>
      </div>
    </section>

    <section class="lp-section lp-final">
      <div class="lp-container lp-reveal">
        <h2 class="lp-h2 lp-final-h">Start running your agency smarter.</h2>
        <p class="lp-sub lp-final-sub">5 free generations. No card. No commitment.</p>
        <button class="btn primary lp-btn-lg" data-cta="signup">Start Free Trial</button>
      </div>
    </section>

    <footer class="lp-footer">
      <div class="lp-container lp-footer-inner">
        <span>© 2026 Managen</span>
        <span class="lp-dot">·</span>
        <a href="/privacy">Privacy Policy</a>
        <span class="lp-dot">·</span>
        <a href="/terms">Terms of Service</a>
      </div>
    </footer>
  </div>`;

  // Wire every CTA to the app entry point.
  document.querySelectorAll('#root [data-cta]').forEach(btn => {
    btn.addEventListener('click', () => onCta(btn.dataset.cta === 'signin' ? 'signin' : 'signup'));
  });

  // Fade-in on scroll.
  const reveals = document.querySelectorAll('.lp-reveal');
  if ('IntersectionObserver' in window) {
    const io = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });
    reveals.forEach(el => io.observe(el));
  } else {
    reveals.forEach(el => el.classList.add('in'));
  }
}

// Remove landing-only body/root state (used before mounting the app in the same session).
export function teardownLanding() {
  document.body.classList.remove('landing-active');
  document.getElementById('root')?.classList.remove('landing-active');
}
